import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import ShareIcon from "@mui/icons-material/Share";
import { Box, Checkbox, IconButton, Tooltip, Typography, Collapse, Menu, MenuItem, ListItemText, Popover } from "@mui/material";
import classNames from "classnames";
import React, { Children, cloneElement, useEffect, useState } from "react";
import InfoIcon from "../../icon/InfoIcon";
import HtmlTooltip from "../../shared/HtmlTooltip";
import ReportButton from "../../shared/ReportButton";
import styles from "./Card.module.scss";
import CardTooltipContent from "./CardTooltipContent";
import CommonIconButtons from "./RightAction";

const MobileCard = ({
	children,
	flex,
	title,
	secondaryTitle = "",
	secondaryTitleError = "",
	secondaryTitleClassName = "",
	minimizable,
	itemAlign,
	isDetailsOpen = true,
	isDisabled = false,
	handleOnClickAction,
	taggedList,
	loading = false,
	isSlider = false,
	tooltipContent = undefined,
	isMinimized,
	priorityNumber = null,
	setIsMinimized = () => void 0,
	handleOnClickReport,
	style,
	styleContent,
	canBeSearched,
	data,
	selectedItems,
	handleToggleAll,
	type,
	debug,
	pageType,
	cardType = null,
	selectAllOption = true,
	customCardData = null,
	sharedUsers = [],
	isSharedWithEveryoneFallback = false,
	handleOnClick,
	disableInteractions = false,
	isDebug = false,
	filterMenuData = null
}) => {
	const [showDetails, setShowDetails] = useState(false);
	const [isMobileExpanded, setIsMobileExpanded] = useState(false);
	const [searchValue, setSearchValue] = useState(""); // eslint-disable-line
	const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
	const [shareAnchorEl, setShareAnchorEl] = useState(null);
	// const usePropsMinimizeStatus = isMinimized !== undefined;

	useEffect(() => {
		setShowDetails(isDisabled ? false : isDetailsOpen);
	}, [isDetailsOpen, isDisabled]);

	useEffect(() => {
		if (!disableInteractions) setShareAnchorEl(null);
	}, [disableInteractions]);

	const allItemsChecked = selectedItems && data && selectedItems.length === data.length && data.length > 0;
	const isSharedWithMe = Boolean(customCardData?.isShared);
	const isSharedWithEveryone = Boolean(customCardData?.isUniversalAccess || customCardData?.isUniversal || isSharedWithEveryoneFallback);
	const shouldShowShareIcon = Boolean(customCardData?._id) && (isSharedWithMe || !isSharedWithEveryone);
	const shareIconVisible = shouldShowShareIcon && disableInteractions;
	const sharedByName =
		customCardData?.shareInfo?.sharedBy?.fullName ||
		customCardData?.sharedBy?.fullName ||
		customCardData?.sharedByName ||
		customCardData?.ownerName ||
		customCardData?.createdBy?.fullName ||
		null;

	const handleOpenShareInfo = (event) => {
		event.stopPropagation();
		setShareAnchorEl(event.currentTarget);
	};

	const handleCloseShareInfo = () => {
		setShareAnchorEl(null);
	};

	// Menu handlers
	const handleFilterMenuOpen = (event) => {
		event.stopPropagation();
		setFilterMenuAnchor(event.currentTarget);
	};

	const handleFilterMenuClose = () => {
		setFilterMenuAnchor(null);
	};

	// Simplified children handling - pass props to all children
	const childrenWithProps = children ? Children.map(children, (child) => {
		if (child && typeof child.type === "function") {
			return cloneElement(child, {
				selectedItems,
				handleToggleAll,
				allItemsChecked,
				loading,
				debug,
				searchValue,
			});
		}
		return child;
	}) : children;

	return (
		<div
			className={classNames(
				styles.card,
				isDisabled && styles.disabled,
				styles.mobileCard,
				isMobileExpanded && styles.mobileExpanded
			)}
			style={{
				...style,
				// Ensure card only takes header height when not expanded
				height: isMobileExpanded ? 'auto' : 'fit-content',
				minHeight: isMobileExpanded ? 'auto' : 'auto',
				overflow: 'hidden'
			}}
		>
			{/* Header - Always visible */}
			<div className={classNames("df aic", styles.cardHeader)}>

				<>
					{/* Filter icon - shows only when NOT expanded */}
					{!isMobileExpanded && filterMenuData?.selectedItem?.length > 0 && (
						<Tooltip title="Filter">
							<IconButton
								disableFocusRipple={true}
								disableRipple={true}
								onClick={handleFilterMenuOpen}
								sx={{
									cursor: loading || disableInteractions ? "default" : "pointer",
									p: 0.5,
									mr: "8px",
									position: "relative"
								}}
								disabled={disableInteractions}
							>
								<FilterListIcon sx={{ fontSize: 18 }} />
								{priorityNumber && (
									<div
										style={{
											position: "absolute",
											top: "-4px",
											right: "-4px",
											background: "#1976d2",
											color: "white",
											borderRadius: "50%",
											width: "16px",
											height: "16px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: "10px",
											fontWeight: "bold",
											lineHeight: 1
										}}
									>
										{priorityNumber}
									</div>
								)}
							</IconButton>
						</Tooltip>
					)}

					{/* Select All checkbox - shows only when expanded */}
					{canBeSearched && isMobileExpanded && ((minimizable && showDetails) || !minimizable) && (
						<Tooltip title="Select All">
							<Checkbox
								disableRipple
								edge="end"
								icon={<CheckCircleOutlineIcon />}
								checkedIcon={<CheckCircleIcon />}
								onChange={disableInteractions ? undefined : () => handleToggleAll({ items: data, type, cardTitle: title })}
								onClick={(e) => e.stopPropagation()} // Prevent card expansion
								checked={allItemsChecked && !loading}
								disabled={loading || disableInteractions}
								sx={{ p: 0, mr: "5px", pl: 0, ml: "-7px", borderRadius: "10000px" }}
							/>
						</Tooltip>
					)}
					{tooltipContent && (
						<div className={styles.cardTooltipIcon}>
							<HtmlTooltip content={!loading && <CardTooltipContent content={tooltipContent} />}>
								<IconButton
									disableFocusRipple={true}
									disableRipple={true}
									onClick={(e) => e.stopPropagation()} // Prevent card expansion
									sx={{ cursor: loading || disableInteractions ? "default" : "pointer" }}
									disabled={disableInteractions}
								>
									<InfoIcon />
								</IconButton>
							</HtmlTooltip>
						</div>
					)}

					{/* Header background - clickable area for expansion */}
					<div
						className="df aic"
						style={{
							flex: 1,
							cursor: disableInteractions ? "default" : "pointer",
							padding: "10px 0"
						}}
						onClick={() => {
							if (!disableInteractions) {
								setIsMobileExpanded(!isMobileExpanded);
							}
						}}
					>
						<Typography
							className={styles.title}
							variant="subtitle1"
							component="div"
							sx={{
								maxWidth: "92%",
								width: "100%",
								fontSize: { xs: "0.8rem", md: "0.8rem" },
								display: "flex",
								flexDirection: "column",
								alignItems: "stretch",
								gap: 0.25,
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									flexWrap: "nowrap",
									justifyContent: "flex-start",
									alignItems: "flex-start",
									columnGap: "18px",
									minWidth: 0,
									width: "100%",
								}}
							>
								<Box
									component="span"
									sx={{
										flex: "0 1 auto",
										minWidth: 0,
										maxWidth: shareIconVisible ? "calc(100% - 40px)" : "100%",
										textAlign: "left",
										overflowWrap: "anywhere",
										wordBreak: "break-word",
									}}
								>
									{title}
								</Box>
								{shareIconVisible && (
									<Box
										sx={{ flex: "0 0 auto", display: "flex", alignItems: "flex-start", pt: "1px" }}
										onMouseDown={(e) => e.stopPropagation()}
									>
										<IconButton
											disableFocusRipple
											disableRipple
											onClick={handleOpenShareInfo}
											sx={{
												width: 22,
												height: 21,
												p: 0,
												borderRadius: "50%",
												border: "2px solid #4879f5",
												backgroundColor: "#fff",
												color: "#4879f5",
												transition:
													"background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
												"&:hover": {
													backgroundColor: "rgba(72, 121, 245, 0.12)",
													borderColor: "#3568d4",
													color: "#3568d4",
												},
											}}
										>
											<ShareIcon sx={{ width: "16px", height: "14px" }} />
										</IconButton>
										<Popover
											open={Boolean(shareAnchorEl)}
											anchorEl={shareAnchorEl}
											onClose={handleCloseShareInfo}
											anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
											transformOrigin={{ vertical: "top", horizontal: "left" }}
											onClick={(e) => e.stopPropagation()}
										>
											<Box sx={{ p: 1.5, minWidth: 220, maxWidth: 320 }}>
												<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
													{isSharedWithMe ? "Shared by" : "Shared with"}
												</Typography>
												{isSharedWithMe ? (
													<Typography variant="body2" sx={{ mb: 0.5 }}>
														{sharedByName || "Unknown"}
													</Typography>
												) : sharedUsers.length > 0 ? (
													sharedUsers.map((userName) => (
														<Typography key={userName} variant="body2" sx={{ mb: 0.5 }}>
															- {userName}
														</Typography>
													))
												) : (
													<Typography variant="body2" color="text.secondary">
														Not shared with anyone
													</Typography>
												)}
											</Box>
										</Popover>
									</Box>
								)}
							</Box>
							{/* &nbsp; &nbsp;<b>{priorityNumber && `#${priorityNumber}`}</b> */}
							{secondaryTitle && <div className={classNames("fs10", styles.secondaryTitle)}>{secondaryTitle}</div>}
							{secondaryTitleError && (
								<div className={classNames("fs10", styles.secondaryTitle, secondaryTitleClassName)}>
									{secondaryTitleError}
								</div>
							)}
						</Typography>
					</div>

					<div className={`df aic mla`} onClick={(e) => e.stopPropagation()}>
						{handleOnClickReport &&
							(loading ? (
								<div
									style={{
										width: "22px",
										height: "22px",
										borderRadius: "50%",
										marginRight: "7px",
									}}
									className="skeleton"
								></div>
							) : (
								<ReportButton
									customCardData={customCardData}
									cardType={cardType || type}
									handleOnClickReport={disableInteractions ? undefined : handleOnClickReport}
									title={title}
									sx={{
										width: 23,
										height: 23,
									}}
									pageType={pageType}
									openFrom="card"
									disabled={disableInteractions}
								/>
							))}
						{!isDisabled && (
							<CommonIconButtons
								title={title}
								handleOnClickAction={disableInteractions ? undefined : handleOnClickAction}
								loading={loading}
								disabled={disableInteractions}
								isMobile={true}
							/>
						)}
						{/* {minimizable && (
							<div
								className="p-l-9 p-r-5 p-t-5 p-b-5 m-l-15"
								style={{ cursor: disableInteractions ? "default" : "pointer" }}
								onClick={(e) => {
									e.stopPropagation(); // Prevent card expansion on mobile
									if (!disableInteractions) {
										usePropsMinimizeStatus ? setIsMinimized(!isMinimized) : setShowDetails(!showDetails);
									}
								}}
							>
								{showStatus ? <MinimizeSVG /> : <PopupPlusSVG />}
							</div>
						)} */}
					</div>
				</>
			</div>

			{/* Tagged list - Always visible if exists */}
			{/* {taggedList && taggedList} */}

			{/* Filter Menu - shows tagged list data as menu items */}
			<Menu
				anchorEl={filterMenuAnchor}
				open={Boolean(filterMenuAnchor)}
				onClose={handleFilterMenuClose}
				PaperProps={{
					sx: {
						maxHeight: 300,
						width: 250
					}
				}}
			>
				{filterMenuData && filterMenuData?.data && filterMenuData?.selectedItem?.length > 0 ? (
					(() => {
						const { data, selectedItem, labelName = "label" } = filterMenuData;
						// Apply the same logic as TaggedList component
						if (selectedItem && selectedItem.length > 0) {

							// Show only selected items
							return data
								.filter(item => selectedItem.includes(item._id))
								.map((item, index) => (
									<MenuItem
										dense
										key={index}
										onClick={handleFilterMenuClose}
										sx={{ fontSize: '14px' }}
									>
										<ListItemText
											primary={item[labelName] || item.name || item.label || 'Unknown'}
										/>
									</MenuItem>
								));
						} else {
							// Show all items if no selection logic applies
							return data.map((item, index) => (
								<MenuItem
									key={index}
									onClick={handleFilterMenuClose}
									sx={{ fontSize: '14px' }}
								>
									<ListItemText
										primary={item[labelName] || item.name || item.label || 'Unknown'}
									/>
								</MenuItem>
							));
						}
					})()
				) : (
					<MenuItem onClick={handleFilterMenuClose}>
						<ListItemText primary="No filter options available" />
					</MenuItem>
				)}
			</Menu>

			{/* Content - Only visible when expanded */}
			<Collapse
				in={isMobileExpanded}
				timeout={300}
				easing="ease-in-out"
				sx={{
					// Ensure collapsed content takes no space
					'&.MuiCollapse-hidden': {
						visibility: 'hidden',
						height: 0,
						overflow: 'hidden'
					},
					'&.MuiCollapse-entered': {
						overflow: 'visible'
					}
				}}
			>
				{/* Content container */}
				<div style={{ padding: '0px 2px' }} onClick={(e) => e.stopPropagation()}>
					{!isSlider && (
						<div
							className={classNames("df", styles.cardContent, itemAlign ? itemAlign : "aic")}
							{...(styleContent && { style: styleContent })}
						>
							{childrenWithProps}
						</div>
					)}
					{isSlider && childrenWithProps}
				</div>
			</Collapse>
		</div>
	);
};

export default MobileCard; 